import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CurpContext } from '../context/CurpContext';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';

const BurstRing = ({ burstKey }) => (
    <AnimatePresence>
        {burstKey && (
            <motion.span
                key={burstKey}
                className="pointer-events-none absolute rounded-xl"
                style={{ inset: 0, border: '1px solid #f8485e', boxShadow: '0 0 6px 1px rgba(248,72,94,0.2)' }}
                initial={{ opacity: 0.5, scale: 1 }}
                animate={{ opacity: 0, scale: 1.25 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.9, ease: 'easeOut' }}
            />
        )}
    </AnimatePresence>
);

const CounselingForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { curp } = useContext(CurpContext);
    const topRef = useRef(null);

    const isSimplified = location.pathname === '/events/counseling/simplified';
    const [hiddeQuestion] = useState(isSimplified);

    const url = isSimplified
        ? 'https://admin-django-inspira.azurewebsites.net/api/services/register-counseling/?simplified=true'
        : 'https://admin-django-inspira.azurewebsites.net/api/services/register-counseling/';

    // WIZARD STATE
    const [currentStep, setCurrentStep] = useState(0);
    const totalSteps = 4;
    const [direction, setDirection] = useState(1);
    const [burstKey, setBurstKey] = useState(null);

    const [formData, setFormData] = useState({
        consent_to_share_data: null,
        consent_for_follow_up: null,
        hiv_tested: null,
        last_hiv_test_date: '',
        last_hiv_test_result: '',
        had_its_last_year: null,
        last_year_its_type: '',
        has_lifetime_its: null, // Temporary field for UI logic
        lifetime_its_diagnosis: '',
        barrier_method_last_sex: '',
        condom_usage_frequency: '',
        other_hiv_prevention_methods: '',
        prep_user: '',
        hiv_test_reason: '',
        is_sex_worker: null,
        sex_affective_partners: [],
        substance_use: '',
        sex_with_substance_user: ''
    });

    const sexAffectivePartnersList = [
        'Mujeres', 'Hombres', 'Mujeres y hombres', 'Mujeres trans', 'Hombres trans', 'Personas no binarias',
    ];

    const inputClass = "border p-2 rounded-xl w-full drop-shadow-sm mt-5 bg-white/50 focus:bg-white focus:ring-2 focus:ring-[#f8485e]/20 outline-none transition-all";
    const textClass = "text-base font-semibold mb-6 text-justify text-gray-800 block";
    const textClass2 = "mt-2 lg:mt-8 mb-1 font-bold text-left text-gray-700";
    const litteTextClass = "text-xs text-left text-gray-400 font-medium uppercase tracking-wider";
    const buttonsContainer = "flex w-full space-x-4 mt-5 mb-8";

    // LOAD DRAFT
    useEffect(() => {
        const saved = localStorage.getItem('counselingDraft');
        if (saved) {
            try {
                setFormData(JSON.parse(saved));
            } catch (e) {
                console.error("Failed to parse draft");
            }
        }
    }, []);

    // SAVE DRAFT
    useEffect(() => {
        localStorage.setItem('counselingDraft', JSON.stringify(formData));
    }, [formData]);

    const scrollToNextField = (nextFieldId) => {
        if (!nextFieldId) return;
        setTimeout(() => {
            const el = document.getElementById(nextFieldId);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 300);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({ ...formData, [name]: type === 'checkbox' ? checked : (value === '' ? null : value) });
    };

    const handleButtonClick = (name, value, nextFieldId) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        setBurstKey(`${name}-${value}-${Date.now()}`);
        if (nextFieldId) scrollToNextField(nextFieldId);
    };

    const handleButtonClick2 = (field, value) => {
        setFormData((prevData) => {
            const currentValues = prevData[field] || [];
            if (currentValues.includes(value)) {
                return { ...prevData, [field]: currentValues.filter((item) => item !== value) };
            }
            return { ...prevData, [field]: [...currentValues, value] };
        });
    };

    // VALIDATION
    const isStepValid = (stepIndex) => {
        switch (stepIndex) {
            case 0:
                return formData.consent_to_share_data !== null && formData.consent_for_follow_up !== null;
            case 1:
                let valid1 = true;
                if (!hiddeQuestion) {
                    if (formData.hiv_tested === null) valid1 = false;
                    if (formData.hiv_tested === true && (!formData.last_hiv_test_date || !formData.last_hiv_test_result)) valid1 = false;
                }
                if (formData.had_its_last_year === null) valid1 = false;
                if (formData.had_its_last_year === true && !formData.last_year_its_type) valid1 = false;
                if (formData.has_lifetime_its === null) valid1 = false;
                if (formData.has_lifetime_its === true && !formData.lifetime_its_diagnosis) valid1 = false;
                return valid1;
            case 2:
                let valid2 = true;
                if (!hiddeQuestion) {
                    if (!formData.barrier_method_last_sex || !formData.condom_usage_frequency || !formData.hiv_test_reason) valid2 = false;
                    if (!formData.other_hiv_prevention_methods?.trim()) valid2 = false;
                }
                // prep_user is optional in checks but we can enforce it if needed. Leaving it optional as before.
                return valid2;
            case 3:
                return formData.is_sex_worker !== null && formData.sex_affective_partners.length > 0 && !!formData.substance_use && !!formData.sex_with_substance_user;
            default:
                return false;
        }
    };

    // Auto-scroll
    useEffect(() => {
        if (topRef.current) {
            topRef.current.scrollIntoView({ behavior: 'instant', block: 'start' });
        }
    }, [currentStep]);

    if (!curp) {
        toast.error('El CURP no está disponible. Redirigiendo a la página principal...');
        setTimeout(() => navigate('/'), 3000);
        return null;
    }

    const nextStep = () => {
        if (isStepValid(currentStep)) {
            setDirection(1);
            setCurrentStep((prev) => prev + 1);
        }
    };

    const prevStep = () => {
        setDirection(-1);
        setCurrentStep((prev) => prev - 1);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isStepValid(3)) return;

        const counselingData = { ...formData, curp: curp };
        delete counselingData.has_lifetime_its; // Remove temporary UI field

        if (location.pathname === '/events/counseling' || location.pathname === '/events/counseling/simplified') {
            const eventId = new URLSearchParams(location.search).get('eventId');
            if (!eventId) {
                toast.error('No se encontró el ID del evento.');
                return;
            }

            const eventRegistrationData = { curp: curp, event_id: eventId };

            toast.promise(
                Promise.all([
                    fetch('https://admin-django-inspira.azurewebsites.net/api/events/register-event/', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(eventRegistrationData),
                    }),
                    fetch(url, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(counselingData),
                    }),
                ]).then(async ([eventResponse, counselingResponse]) => {
                    if (!eventResponse.ok || !counselingResponse.ok) {
                        const eventError = await eventResponse.json().catch(() => null);
                        const counselingError = await counselingResponse.json().catch(() => null);
                        throw new Error(eventError?.detail || counselingError?.detail || 'Hubo un problema.');
                    }
                    localStorage.removeItem('counselingDraft');
                    navigate('/thank-you');
                    return '¡Registro exitoso! 🎉';
                }),
                {
                    loading: 'Lxs gatitxs están organizando tu información... 🐱📁',
                    success: (message) => message,
                    error: (err) => `Error: ${err.message}`,
                }
            );
        } else {
            toast.promise(
                fetch('https://admin-django-inspira.azurewebsites.net/api/services/register-counseling/', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(counselingData),
                }).then(async (response) => {
                    if (response.status === 201) {
                        localStorage.removeItem('counselingDraft');
                        if (formData.substance_use === 'No') {
                            navigate('/services/counseling/appointment');
                            return 'Genial, ¿Cuándo nos visitas? 🗓️';
                        } else {
                            navigate('/services/counseling/substance');
                            return 'Vamos con el formulario de consumo de sustancias 🧪';
                        }
                    } else {
                        const errorData = await response.json();
                        throw new Error(errorData.detail || 'Ay, algo pasó, intenta nuevamente.');
                    }
                }),
                {
                    loading: 'Guardando información... 🐾💻',
                    success: (message) => message,
                    error: (err) => `Error: ${err.message}`,
                }
            );
        }
    };

    const variants = {
        enter: (direction) => ({ x: direction > 0 ? 1000 : -1000, opacity: 0 }),
        center: { zIndex: 1, x: 0, opacity: 1 },
        exit: (direction) => ({ zIndex: 0, x: direction < 0 ? 1000 : -1000, opacity: 0 })
    };

    const RadioButtons = ({ field, options, nextFieldId, gridCols = 1 }) => (
        <div className={`grid grid-cols-1 md:grid-cols-${gridCols} gap-2 mt-1`}>
            {options.map((opt) => (
                <div key={opt.value} className="relative">
                    <motion.button type="button" whileTap={{ scale: 0.95 }}
                        onClick={() => handleButtonClick(field, opt.value, nextFieldId)}
                        className={`w-full h-full rounded-xl border-2 drop-shadow-sm p-4 text-center font-bold uppercase transition-all text-xs ${formData[field] === opt.value ? 'bg-[#f8485e] text-white border-[#f8485e] shadow-[0_4px_14px_rgba(248,72,94,0.3)]' : 'bg-white text-gray-500 border-gray-200 hover:border-[#f8485e]/40'}`}
                    >
                        {opt.label}
                    </motion.button>
                    <BurstRing burstKey={burstKey?.startsWith(`${field}-${opt.value}`) ? burstKey : null} />
                </div>
            ))}
        </div>
    );

    const renderStep0 = () => (
        <div className="w-full">
            <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} className='text-3xl sm:text-4xl lg:text-5xl font-bold text-[#f8485e] mt-3 drop-shadow-sm uppercase leading-tight text-center mb-10'>Consentimientos</motion.h1>
            
            <div id="couns-q1" className="mb-6">
                <label className={textClass}>En caso de que mi resultado sea reactivo a alguna de las pruebas solicitadas, otorgo mi consentimiento para que mis datos puedan ser compartidos con el CENSIDA.</label>
                <div className={buttonsContainer}>
                    {[true, false].map((val) => (
                        <div key={String(val)} className='w-1/2 flex justify-center relative'>
                            <motion.button type="button" whileTap={{ scale: 0.95 }}
                                onClick={() => handleButtonClick('consent_to_share_data', val, 'couns-q2')}
                                className={`w-full rounded-xl border-2 drop-shadow-sm p-4 text-center font-bold uppercase transition-all ${formData.consent_to_share_data === val ? 'bg-[#f8485e] text-white border-[#f8485e]' : 'bg-white text-gray-500 border-gray-200'}`}
                            >
                                {val ? 'Acepto' : 'No acepto'}
                            </motion.button>
                            <BurstRing burstKey={burstKey?.startsWith(`consent_to_share_data-${val}`) ? burstKey : null} />
                        </div>
                    ))}
                </div>
            </div>

            <div id="couns-q2" className="mb-6">
                <label className={textClass}>En caso de un resultado reactivo estoy de acuerdo con que se realice el proceso de seguimiento por parte de la organización.</label>
                <div className={buttonsContainer}>
                    {[true, false].map((val) => (
                        <div key={String(val)} className='w-1/2 flex justify-center relative'>
                            <motion.button type="button" whileTap={{ scale: 0.95 }}
                                onClick={() => handleButtonClick('consent_for_follow_up', val)}
                                className={`w-full rounded-xl border-2 drop-shadow-sm p-4 text-center font-bold uppercase transition-all ${formData.consent_for_follow_up === val ? 'bg-[#f8485e] text-white border-[#f8485e]' : 'bg-white text-gray-500 border-gray-200'}`}
                            >
                                {val ? 'Acepto' : 'No acepto'}
                            </motion.button>
                            <BurstRing burstKey={burstKey?.startsWith(`consent_for_follow_up-${val}`) ? burstKey : null} />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderStep1 = () => (
        <div className="w-full">
            <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} className='text-3xl sm:text-4xl lg:text-5xl font-bold text-[#f8485e] mt-3 drop-shadow-sm uppercase leading-tight text-center mb-10'>Historial</motion.h1>
            
            {!hiddeQuestion && (
                <>
                    <div id="couns-q3" className="mb-6">
                        <label className={textClass}>¿Te has hecho una prueba de VIH?</label>
                        <div className="flex flex-col gap-3 mt-1">
                            {[true, false].map((val) => (
                                <div key={String(val)} className='w-full relative'>
                                    <motion.button type="button" whileTap={{ scale: 0.95 }}
                                        onClick={() => handleButtonClick('hiv_tested', val, val ? 'couns-q3a' : 'couns-q4')}
                                        className={`w-full rounded-xl border-2 drop-shadow-sm p-4 text-center text-sm font-bold uppercase transition-all ${formData.hiv_tested === val ? 'bg-[#f8485e] text-white border-[#f8485e]' : 'bg-white text-gray-500 border-gray-200'}`}
                                    >
                                        {val ? 'Sí, ya me he hecho la prueba antes' : 'No, nunca me he hecho la prueba'}
                                    </motion.button>
                                    <BurstRing burstKey={burstKey?.startsWith(`hiv_tested-${val}`) ? burstKey : null} />
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    <AnimatePresence>
                        {formData.hiv_tested && (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                                <div id="couns-q3a" className="mb-8">
                                    <label className={textClass}>¿Cuándo fue la última vez que te hiciste la prueba?</label>
                                    <RadioButtons field="last_hiv_test_date" nextFieldId="couns-q3b" options={[
                                        { value: "0", label: "En los últimos 6 meses" },
                                        { value: "1", label: "En los últimos 7 - 12 meses" },
                                        { value: "2", label: "Hace más de 12 meses" }
                                    ]} />
                                </div>
                                <div id="couns-q3b" className="mb-8">
                                    <label className={textClass}>¿Cuál fue el resultado de las últimas pruebas para el VIH que te hiciste?</label>
                                    <RadioButtons field="last_hiv_test_result" nextFieldId="couns-q4" options={[
                                        { value: "0", label: "Positivo/Reactivo" },
                                        { value: "1", label: "Negativo/No reactivo" },
                                        { value: "2", label: "Indeterminado" }
                                    ]} />
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </>
            )}

            <div id="couns-q4" className="mb-6">
                <label className={textClass}>¿En el ÚLTIMO AÑO has tenido alguna infección de transmisión sexual (ITS) que no sea VIH?</label>
                <div className={buttonsContainer}>
                    {[true, false].map((val) => (
                        <div key={String(val)} className='w-1/2 flex justify-center relative'>
                            <motion.button type="button" whileTap={{ scale: 0.95 }}
                                onClick={() => handleButtonClick('had_its_last_year', val, val ? 'couns-q4a' : 'couns-q5')}
                                className={`w-full rounded-xl border-2 drop-shadow-sm p-4 text-center font-bold uppercase transition-all ${formData.had_its_last_year === val ? 'bg-[#f8485e] text-white border-[#f8485e]' : 'bg-white text-gray-500 border-gray-200'}`}
                            >
                                {val ? 'Sí' : 'No'}
                            </motion.button>
                            <BurstRing burstKey={burstKey?.startsWith(`had_its_last_year-${val}`) ? burstKey : null} />
                        </div>
                    ))}
                </div>
            </div>

            <AnimatePresence>
                {formData.had_its_last_year && (
                    <motion.div id="couns-q4a" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-8 overflow-hidden">
                        <label className={textClass}>¿De qué ITS se trató?</label>
                        <RadioButtons field="last_year_its_type" nextFieldId="couns-q5" gridCols={2} options={[
                            { value: "0", label: "Sífilis" }, { value: "1", label: "Gonorrea" }, { value: "2", label: "Clamidia" },
                            { value: "3", label: "Candidiasis" }, { value: "4", label: "Hepatitis C" }, { value: "5", label: "Hepatitis B" },
                            { value: "6", label: "VPH" }, { value: "7", label: "Herpes" }, { value: "8", label: "Molusco contagioso" },
                            { value: "9", label: "Infección Vaginal" }, { value: "10", label: "MPOX" }
                        ]} />
                    </motion.div>
                )}
            </AnimatePresence>

            <div id="couns-q5" className="mb-6">
                <label className={textClass}>¿A lo largo de tu vida has sido diagnosticado con alguna otra ITS?</label>
                <div className={buttonsContainer}>
                    {[true, false].map((val) => (
                        <div key={String(val)} className='w-1/2 flex justify-center relative'>
                            <motion.button type="button" whileTap={{ scale: 0.95 }}
                                onClick={() => handleButtonClick('has_lifetime_its', val, val ? 'couns-q5a' : null)}
                                className={`w-full rounded-xl border-2 drop-shadow-sm p-4 text-center font-bold uppercase transition-all ${formData.has_lifetime_its === val ? 'bg-[#f8485e] text-white border-[#f8485e]' : 'bg-white text-gray-500 border-gray-200'}`}
                            >
                                {val ? 'Sí' : 'No'}
                            </motion.button>
                            <BurstRing burstKey={burstKey?.startsWith(`has_lifetime_its-${val}`) ? burstKey : null} />
                        </div>
                    ))}
                </div>
            </div>

            <AnimatePresence>
                {formData.has_lifetime_its && (
                    <motion.div id="couns-q5a" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-8 overflow-hidden">
                        <label className={textClass}>¿De qué ITS se trató?</label>
                        <RadioButtons field="lifetime_its_diagnosis" gridCols={2} options={[
                            { value: "0", label: "Sífilis" }, { value: "1", label: "Gonorrea" }, { value: "2", label: "Clamidia" },
                            { value: "3", label: "Candidiasis" }, { value: "4", label: "Hepatitis C" }, { value: "5", label: "Hepatitis B" },
                            { value: "6", label: "VPH" }, { value: "7", label: "Herpes" }, { value: "8", label: "Molusco contagioso" },
                            { value: "9", label: "Infección Vaginal" }, { value: "10", label: "MPOX" }
                        ]} />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );

    const renderStep2 = () => (
        <div className="w-full">
            <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} className='text-3xl sm:text-4xl lg:text-5xl font-bold text-[#f8485e] mt-3 drop-shadow-sm uppercase leading-tight text-center mb-10'>Prácticas</motion.h1>
            
            {!hiddeQuestion && (
                <>
                    <div id="couns-q-barrier" className="mb-8">
                        <label className={textClass}>¿En la última relación sexual que tuviste (anal o vaginal) usaste algún método de barrera como condón interno o externo?</label>
                        <RadioButtons field="barrier_method_last_sex" nextFieldId="couns-q-condom" options={[
                            { value: "Si", label: "Sí" },
                            { value: "No", label: "No" },
                            { value: "No lo recuerdo", label: "No lo recuerdo" }
                        ]} />
                    </div>
                    <div id="couns-q-condom" className="mb-8">
                        <label className={textClass}>¿Regularmente qué tanto usas el condón en tus relaciones sexuales penetrativas?</label>
                        <RadioButtons field="condom_usage_frequency" nextFieldId="couns-q-other" gridCols={1} options={[
                            { value: "0", label: "Siempre uso condón" },
                            { value: "1", label: "Más de la mitad de las veces" },
                            { value: "2", label: "La mitad de las veces" },
                            { value: "3", label: "Menos de la mitad de las veces" },
                            { value: "4", label: "Nunca uso condón" }
                        ]} />
                    </div>
                    <div id="couns-q-other" className="mb-8">
                        <label className={textClass}>¿Qué otros métodos de prevención para el VIH que no sea el condón conoces?</label>
                        <input type="text" name="other_hiv_prevention_methods" value={formData.other_hiv_prevention_methods} onChange={handleChange} className={inputClass} required />
                    </div>
                </>
            )}

            <div id="couns-q-prep" className="mb-8">
                <label className={textClass}>¿Eres usuario de la Profilaxis pre-exposición (PrEP)?</label>
                <RadioButtons field="prep_user" nextFieldId="couns-q-reason" options={[
                    { value: "Si", label: "Sí" },
                    { value: "No", label: "No" },
                    { value: "No sabe", label: "No sé qué es 🤷🏽‍♀️" }
                ]} />
            </div>

            {!hiddeQuestion && (
                <div id="couns-q-reason" className="mb-8">
                    <label className={textClass}>¿Por qué motivo te estás realizando la prueba de VIH?</label>
                    <RadioButtons field="hiv_test_reason" gridCols={1} options={[
                        { value: "0", label: "Es la primera vez que me la hago, quiero conocer mi estatus" },
                        { value: "1", label: "Tuve una práctica de riesgo y quiero conocer mi estatus" },
                        { value: "2", label: "Una de mis parejas sexuales fue diagnosticada con VIH y quiero conocer mi estatus" },
                        { value: "3", label: "Estoy tomando PrEP/PEP y es una prueba de rutina por protocolo" },
                        { value: "4", label: "Mi pareja sentimental fue diagnosticada con VIH y quiero conocer mi estatus" },
                        { value: "5", label: "Por rutina, me hago la prueba regularmente (al menos 2 veces al año)" },
                        { value: "6", label: "Personal médico me sugirió hacerme la prueba de VIH" },
                        { value: "7", label: "Tengo la sospecha de vivir con VIH y quiero conocer mi estatus" },
                        { value: "8", label: "Otra razón" },
                        { value: "9", label: "No voy a realizarme la prueba" }
                    ]} />
                </div>
            )}
        </div>
    );

    const renderStep3 = () => (
        <div className="w-full">
            <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} className='text-3xl sm:text-4xl lg:text-5xl font-bold text-[#f8485e] mt-3 drop-shadow-sm uppercase leading-tight text-center mb-10'>Relaciones</motion.h1>
            
            <div id="couns-q6" className="mb-8">
                <label className={textClass}>¿Actualmente ejerces trabajo sexual?</label>
                <div className={buttonsContainer}>
                    {[true, false].map((val) => (
                        <div key={String(val)} className='w-1/2 flex justify-center relative'>
                            <motion.button type="button" whileTap={{ scale: 0.95 }}
                                onClick={() => handleButtonClick('is_sex_worker', val, 'couns-q7')}
                                className={`w-full rounded-xl border-2 drop-shadow-sm p-4 text-center font-bold uppercase transition-all ${formData.is_sex_worker === val ? 'bg-[#f8485e] text-white border-[#f8485e]' : 'bg-white text-gray-500 border-gray-200'}`}
                            >
                                {val ? 'Sí' : 'No'}
                            </motion.button>
                            <BurstRing burstKey={burstKey?.startsWith(`is_sex_worker-${val}`) ? burstKey : null} />
                        </div>
                    ))}
                </div>
            </div>

            <div id="couns-q7" className="mb-8">
                <label className={textClass2}>¿Con quiénes te relacionas sexoafectivamente de forma habitual?</label>
                <label className={litteTextClass}>(Puedes seleccionar más de una opción)</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-1 mt-5">
                    {sexAffectivePartnersList.map((choice) => (
                        <motion.button key={choice} type="button" whileTap={{ scale: 0.95 }} onClick={() => handleButtonClick2('sex_affective_partners', choice)}
                            className={`w-full h-20 px-2 flex flex-col rounded-xl border-2 shadow-sm items-center justify-center text-center text-[10px] sm:text-xs font-bold uppercase transition-all ${
                                formData.sex_affective_partners.includes(choice) ? 'bg-[#f8485e] text-white border-[#f8485e]' : 'bg-white text-gray-500 border-gray-200'
                            }`}
                        >
                            {choice}
                        </motion.button>
                    ))}
                </div>
            </div>

            <div id="couns-q8" className="mb-8">
                <label className={textClass}>¿Consumes algún tipo de sustancia ya sea fumada, inhalada, tomada, inyectada, etc.?</label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                    {[
                        { value: 'Si', label: 'Sí' }, { value: 'No', label: 'No' },
                        { value: 'Ocasional', label: 'Ocasionalmente' }, { value: 'Prefiero no responder', label: 'Prefiero no responder' },
                    ].map((opt) => (
                        <div key={opt.value} className={`relative ${opt.value === 'Prefiero no responder' || opt.value === 'Ocasional' ? 'col-span-2' : ''}`}>
                            <motion.button type="button" whileTap={{ scale: 0.92 }}
                                onClick={() => handleButtonClick('substance_use', opt.value, 'couns-q9')}
                                className={`relative w-full py-4 px-3 rounded-xl font-bold text-xs uppercase tracking-widest transition-all duration-200 border-2 ${
                                    formData.substance_use === opt.value ? 'bg-[#f8485e] text-white border-[#f8485e] shadow-[0_4px_14px_rgba(248,72,94,0.3)]' : 'bg-white border-gray-200 text-gray-400'
                                }`}
                            >{opt.label}</motion.button>
                            <BurstRing burstKey={burstKey?.startsWith(`substance_use-${opt.value}`) ? burstKey : null} />
                        </div>
                    ))}
                </div>
            </div>

            <div id="couns-q9" className="mb-8">
                <label className={textClass}>¿En el último mes tuviste relaciones sexuales con alguna persona que se encontraba bajo el efecto de alguna sustancia?</label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                    {[
                        { value: 'Si', label: 'Sí' }, { value: 'No', label: 'No' }, { value: 'No sabe', label: 'No lo sé' },
                    ].map((opt) => (
                        <div key={opt.value} className={`relative ${opt.value === 'No sabe' ? 'col-span-2' : ''}`}>
                            <motion.button type="button" whileTap={{ scale: 0.92 }}
                                onClick={() => handleButtonClick('sex_with_substance_user', opt.value)}
                                className={`relative w-full py-4 px-3 rounded-xl font-bold text-xs uppercase tracking-widest transition-all duration-200 border-2 ${
                                    formData.sex_with_substance_user === opt.value ? 'bg-[#f8485e] text-white border-[#f8485e] shadow-[0_4px_14px_rgba(248,72,94,0.3)]' : 'bg-white border-gray-200 text-gray-400'
                                }`}
                            >{opt.label}</motion.button>
                            <BurstRing burstKey={burstKey?.startsWith(`sex_with_substance_user-${opt.value}`) ? burstKey : null} />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderCurrentStep = () => {
        switch(currentStep) {
            case 0: return renderStep0();
            case 1: return renderStep1();
            case 2: return renderStep2();
            case 3: return renderStep3();
            default: return null;
        }
    };

    return (
        <div className="w-full text-left mt-0 lg:w-2/3 lg:mb-10 mx-auto">
            <div ref={topRef} className="absolute top-0 w-full h-1" />
            
            {currentStep === 0 && (
                <>
                    <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.5 }} className='text-left text-5xl font-black text-[#f8485e] mt-10 mb-8 leading-tight drop-shadow-sm'>Es hora de la fiesta de preguntas 🎉</motion.h1>
                    <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-sm text-justify font-medium mt-5 mb-5 text-gray-500 leading-relaxed">
                        La información que se te solicita a continuación es confidencial y está protegida por la Ley Federal de Protección de Datos Personales. La finalidad de dichas preguntas es brindarte un servicio de consejería más personalizado, ofrecerte un mejor servicio, conocer a nuestras usuarias y usuarios, y saber a qué población estamos atendiendo. Ayúdanos respondiendo con toda sinceridad y con la confianza de que tu información está protegida y cuidada. 🫡
                    </motion.p>
                </>
            )}

            <div className="w-full mt-6 mb-4">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center mb-2">Paso {currentStep + 1} de {totalSteps}</p>
                <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <motion.div 
                        className="h-full bg-[#f8485e]"
                        initial={{ width: 0 }}
                        animate={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
                        transition={{ duration: 0.3 }}
                    />
                </div>
            </div>

            <form onSubmit={handleSubmit} className="w-full mt-4 overflow-hidden">
                <AnimatePresence mode="wait" custom={direction}>
                    <motion.div
                        key={currentStep}
                        custom={direction}
                        variants={variants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{ x: { type: "spring", stiffness: 300, damping: 30 }, opacity: { duration: 0.2 } }}
                    >
                        {renderCurrentStep()}
                    </motion.div>
                </AnimatePresence>

                <div className="flex justify-between gap-4 mt-8 mb-12">
                    <button type="button" onClick={prevStep}
                        className={`flex-1 py-4 rounded-2xl font-black uppercase tracking-widest transition-all ${
                            currentStep === 0 ? 'opacity-0 pointer-events-none' : 'bg-white border-2 border-gray-200 text-gray-500 hover:border-[#f8485e]/40'
                        }`}
                    >Atrás</button>
                    
                    {currentStep < totalSteps - 1 ? (
                        <button type="button" onClick={nextStep} disabled={!isStepValid(currentStep)}
                            className={`flex-1 py-4 rounded-2xl shadow-xl font-black uppercase tracking-widest transition-all active:scale-95 ${
                                isStepValid(currentStep) ? 'bg-[#f8485e] hover:bg-[#d93a4f] text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
                            }`}
                        >Siguiente ✨</button>
                    ) : (
                        <button type="submit" disabled={!isStepValid(currentStep)}
                            className={`flex-1 py-4 rounded-2xl shadow-xl font-black uppercase tracking-widest transition-all active:scale-95 ${
                                isStepValid(currentStep) ? 'bg-[#f8485e] hover:bg-[#d93a4f] text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
                            }`}
                        >Enviar 🚀</button>
                    )}
                </div>
            </form>
        </div>
    );
};

export default CounselingForm;